import { useCallback, useState, useMemo, useEffect } from 'react';
import { Task } from '@/types/tasks';
import { useAuth } from '@/contexts/auth';
import { useTaskData } from '@/contexts/task/hooks/useTaskData';
import { updateTaskInDatabase } from '@/components/tasks/hooks/task-drag/task-database-operations';
import { DateRangeOption } from '@/hooks/tasks/use-task-operations';
import { useCreateTaskWithAssignees } from '@/hooks/tasks/use-create-task-with-assignees';

export interface GoalTaskContextType {
  activeTasks: Task[];
  completedTasks: Task[];
  activeTaskCount: number;
  isLoading: boolean;
  setActiveTasks: (tasks: Task[]) => void;
  setCompletedTasks: (tasks: Task[]) => void;
  addTask: (task: Omit<Task, 'id' | 'isCompleted' | 'createdAt'>) => void;
  updateTask: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  updateTaskOrder: (taskId: string, newOrder: number) => void;
  completeTask: (taskId: string) => void;
  uncompleteTask: (taskId: string) => void;
  removeTask: (taskId: string) => void;
  refetchTasks: () => void;
  resetToFilteredState: () => void;
}

export const useGoalFilteredTasks = (goalId: string): GoalTaskContextType => {
  const { user } = useAuth();
  
  // Use atomic task creation for goal-specific tasks
  const taskCreationMutation = useCreateTaskWithAssignees();
  
  // Use a separate task data source with NO date filtering for goal views
  const {
    dbActiveTasks: allActiveTasks,
    dbCompletedTasks: allCompletedTasks,
    isLoading,
    dbUpdateTask,
    dbCompleteTask,
    dbUncompleteTask,
    dbRemoveTask,
    refetch
  } = useTaskData(
    'week', // dateRange doesn't matter since we're bypassing filters
    new Date(), // currentDate doesn't matter since we're bypassing filters  
    false, // strictDateRange doesn't matter since we're bypassing filters
    true // noDateFilter: TRUE - This bypasses ALL date filtering!
  );
  
  // Local state for optimistic updates during drag operations
  const [localActiveTasks, setLocalActiveTasks] = useState<Task[]>([]);
  const [localCompletedTasks, setLocalCompletedTasks] = useState<Task[]>([]);
  const [useLocalState, setUseLocalState] = useState(false);
  
  // Filter ALL tasks by goalId (no date restrictions)
  const { activeTasks: filteredActiveTasks, completedTasks: filteredCompletedTasks } = useMemo(() => {
    const goalActiveTasks = allActiveTasks.filter(task => task.goalId === goalId);
    const goalCompletedTasks = allCompletedTasks.filter(task => task.goalId === goalId);
    
    return {
      activeTasks: goalActiveTasks,
      completedTasks: goalCompletedTasks
    };
  }, [allActiveTasks, allCompletedTasks, goalId]);
  
  // Use local state during drag operations, otherwise use filtered global state
  // Properly memoized to trigger re-renders when useLocalState changes
  const activeTasks = useMemo(() => {
    const result = useLocalState ? localActiveTasks : filteredActiveTasks;
    console.log('[useGoalFilteredTasks] activeTasks computed:', { 
      useLocalState, 
      localCount: localActiveTasks.length, 
      filteredCount: filteredActiveTasks.length,
      resultCount: result.length 
    });
    return result;
  }, [useLocalState, localActiveTasks, filteredActiveTasks]);
  
  const completedTasks = useMemo(() => {
    const result = useLocalState ? localCompletedTasks : filteredCompletedTasks;
    console.log('[useGoalFilteredTasks] completedTasks computed:', { 
      useLocalState, 
      localCount: localCompletedTasks.length, 
      filteredCount: filteredCompletedTasks.length,
      resultCount: result.length 
    });
    return result;
  }, [useLocalState, localCompletedTasks, filteredCompletedTasks]);
  
  // Update local state when filtered state changes (but only when not using local state)
  useEffect(() => {
    if (!useLocalState) {
      setLocalActiveTasks(filteredActiveTasks);
      setLocalCompletedTasks(filteredCompletedTasks);
    }
  }, [filteredActiveTasks, filteredCompletedTasks, useLocalState]);
  
  const activeTaskCount = useMemo(() => activeTasks.length, [activeTasks]);
  
  // Wrapper functions that use simple database updates (like TaskManager)
  const updateTask = useCallback(async (taskId: string, updates: Partial<Task>, silent = false) => {
    console.log('[useGoalFilteredTasks] updateTask called:', { taskId, updates, useLocalState });
    
    // CRITICAL: Switch to local state FIRST for immediate UI updates
    setUseLocalState(true);
    
    // Handle completion status changes (moving between lists) - immediate local update
    if (updates.isCompleted !== undefined) {
      const sourceList = updates.isCompleted ? localActiveTasks : localCompletedTasks;
      const targetList = updates.isCompleted ? localCompletedTasks : localActiveTasks;
      const sourceSetFunction = updates.isCompleted ? setLocalActiveTasks : setLocalCompletedTasks;
      const targetSetFunction = updates.isCompleted ? setLocalCompletedTasks : setLocalActiveTasks;
      
      const task = sourceList.find(t => t.id === taskId);
      if (task) {
        const updatedTask = { ...task, ...updates };
        sourceSetFunction(sourceList.filter(t => t.id !== taskId));
        targetSetFunction([...targetList, updatedTask]);
        console.log('[useGoalFilteredTasks] Moved task between lists immediately');
      }
    } else {
      // Handle updates within the same list - immediate local update
      const updateTaskInList = (tasks: Task[]) => 
        tasks.map(task => task.id === taskId ? { ...task, ...updates } : task);
      
      setLocalActiveTasks(updateTaskInList);
      setLocalCompletedTasks(updateTaskInList);
      console.log('[useGoalFilteredTasks] Updated task within list immediately');
    }
    
    // Then update database using simple function (no React Query)
    try {
      await updateTaskInDatabase(taskId, updates);
      console.log('[useGoalFilteredTasks] Database update successful - triggering refetch');
      // CRITICAL: Trigger refetch to update the underlying React Query cache
      refetch();
    } catch (error) {
      console.error('[useGoalFilteredTasks] Database update failed:', error);
      // Revert local state on error
      setLocalActiveTasks(filteredActiveTasks);
      setLocalCompletedTasks(filteredCompletedTasks);
      setUseLocalState(false);
    }
  }, [localActiveTasks, localCompletedTasks, filteredActiveTasks, filteredCompletedTasks, refetch]);
  
  const updateTaskOrder = useCallback(async (taskId: string, newOrder: number) => {
    console.log('[useGoalFilteredTasks] updateTaskOrder called:', { taskId, newOrder, useLocalState });
    
    // CRITICAL: Switch to local state FIRST for immediate UI updates
    setUseLocalState(true);
    
    // Immediate local update for card order changes
    const updateTaskInList = (tasks: Task[]) => 
      tasks.map(task => task.id === taskId ? { ...task, cardOrder: newOrder } : task);
    
    setLocalActiveTasks(updateTaskInList);
    setLocalCompletedTasks(updateTaskInList);
    console.log('[useGoalFilteredTasks] Updated task order locally immediately');
    
    // Then update database using simple function (no React Query)
    try {
      await updateTaskInDatabase(taskId, { cardOrder: Math.round(newOrder) }); // Round to integer
      console.log('[useGoalFilteredTasks] Task order update successful - triggering refetch');
      // CRITICAL: Trigger refetch to update the underlying React Query cache
      refetch();
    } catch (error) {
      console.error('[useGoalFilteredTasks] Task order update failed:', error);
      // Revert local state on error
      setLocalActiveTasks(filteredActiveTasks);
      setLocalCompletedTasks(filteredCompletedTasks);
      setUseLocalState(false);
    }
  }, [filteredActiveTasks, filteredCompletedTasks, refetch]);

  // NEW: Task completion operations using the goal-specific data source
  const completeTask = useCallback((taskId: string) => {
    if (dbCompleteTask) {
      dbCompleteTask(taskId);
    }
  }, [dbCompleteTask]);

  const uncompleteTask = useCallback((taskId: string) => {
    if (dbUncompleteTask) {
      dbUncompleteTask(taskId);
    }
  }, [dbUncompleteTask]);

  const removeTask = useCallback((taskId: string) => {
    if (dbRemoveTask) {
      dbRemoveTask(taskId);
    }
  }, [dbRemoveTask]);
  
  const setActiveTasks = useCallback((tasks: Task[]) => {
    setUseLocalState(true);
    setLocalActiveTasks(tasks);
  }, []);
  
  const setCompletedTasks = useCallback((tasks: Task[]) => {
    setUseLocalState(true);
    setLocalCompletedTasks(tasks);
  }, []);
  
  const refetchTasks = useCallback(() => {
    setUseLocalState(false);
    refetch();
  }, [refetch]);
  
  // Reset to use filtered state (for drag operations)
  const resetToFilteredState = useCallback(() => {
    setUseLocalState(false);
  }, []);
  
  // Goal-specific task creation using atomic function
  const addTask = useCallback(async (task: Omit<Task, 'id' | 'isCompleted' | 'createdAt'>) => {
    try {
      await taskCreationMutation.mutateAsync({
        title: task.title,
        goalId: goalId, // Use the goal ID from the hook
        description: task.description,
        dueDate: task.dueDate,
        priority: task.priority,
        urgency: task.urgency,
        assigneeIds: [] // Will be handled by TaskAssigneeSelector
      });
      // Refresh the goal-filtered tasks
      refetchTasks();
    } catch (error) {
      console.error('Failed to create goal-specific task:', error);
      // Error handling is done by the mutation hook
    }
  }, [taskCreationMutation, goalId, refetchTasks]);
  
  return {
    activeTasks,
    completedTasks,
    activeTaskCount,
    isLoading,
    setActiveTasks,
    setCompletedTasks,
    addTask,
    updateTask,
    updateTaskOrder,
    completeTask,
    uncompleteTask,
    removeTask,
    refetchTasks,
    resetToFilteredState
  };
}; 